// LIVE-10 多螢幕（SPEC #vms-web-live10）的畫面：〔在另一個螢幕開〕、〔還原多螢幕配置〕、彈出視窗上方的精簡列。
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '../../components/Dialog';
import { forgetSpot, layoutKey, listScreens, loadSpots, multiScreen, newWallId, openWall, saveSpots, type PopoutSpot, type ScreenInfo } from '../../lib/multiScreen';
import { useMultiScreenVersion } from '../../lib/useMultiScreen';

export const LIVE_LAYOUT_KEY = 'kinio.vms.liveLayout.v1';

/** 彈出：把目前的版面交給新的那面牆（同源 localStorage，不寫進網址），有 Window Management API 就讓使用者選螢幕。 */
export function PopoutButton({ layout }: { layout: unknown }) {
  const { t } = useTranslation();
  const [screens, setScreens] = useState<ScreenInfo[] | null>(null);
  const [hint, setHint] = useState<string | null>(null);

  function open(screen?: ScreenInfo) {
    const wall = newWallId();
    try {
      localStorage.setItem(layoutKey(LIVE_LAYOUT_KEY, wall), JSON.stringify(layout));
    } catch {
      /* 無 storage：新牆從空的開始 */
    }
    const spot: PopoutSpot = screen ? { wall, left: screen.left, top: screen.top, width: screen.width, height: screen.height, screen: screen.label } : { wall };
    if (!openWall(spot)) {
      setHint(t('live.ms_blocked'));
      return;
    }
    saveSpots([...loadSpots(), spot]);
    setScreens(null);
    setHint(screen ? null : t('live.ms_drag_hint'));
  }

  async function start() {
    setHint(null);
    const list = await listScreens();
    if (list && list.length > 1) setScreens(list);
    else open(); // 沒有 API / 只有一個螢幕 / 使用者拒絕：照樣開新視窗，提示拖過去
  }

  return (
    <>
      <button className="btn btn-sm" onClick={() => void start()} data-testid="ms-popout" title={t('live.ms_popout_hint')}>
        {t('live.ms_popout')}
      </button>
      {hint && (
        <span className="small muted" data-testid="ms-hint">
          {hint}
        </span>
      )}
      <Dialog open={!!screens} title={t('live.ms_pick_screen')} onClose={() => setScreens(null)}>
        <div className="form">
          {(screens ?? []).map((s, i) => (
            <button key={i} className="btn" onClick={() => open(s)} data-screen={s.label}>
              {s.label} · {s.width}×{s.height}
              {s.current ? ` (${t('live.ms_this_screen')})` : ''}
            </button>
          ))}
        </div>
      </Dialog>
    </>
  );
}

/** 主視窗：記住的牆還沒開的話，提供〔還原多螢幕配置〕（瀏覽器規定開視窗要手勢；有權限時一次開全部，沒有就每按一次開一個）。 */
export function RestoreButton() {
  const { t } = useTranslation();
  useMultiScreenVersion();
  const [, force] = useState(0);
  const alive = new Set(multiScreen.peerList().map((p) => p.wall).filter(Boolean));
  const missing = loadSpots().filter((s) => !alive.has(s.wall));
  if (multiScreen.popout || missing.length === 0) return null;
  return (
    <button
      className="btn btn-sm"
      data-testid="ms-restore"
      onClick={() => {
        for (const s of missing) if (!openWall(s)) break;
        force((n) => n + 1);
      }}
    >
      {t('live.ms_restore', { n: missing.length })}
    </button>
  );
}

/** 彈出視窗上方：這面牆、警報在不在這裡跳、關閉這面牆（從還原配置拿掉）。 */
export function PopoutBar({ alarmHere }: { alarmHere: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="popout-bar" data-testid="popout-bar">
      <strong>{t('live.ms_wall')}</strong>
      <span className="muted">{t('live.ms_f11')}</span>
      <span className="spacer" />
      {alarmHere ? (
        <span className="chip" data-testid="ms-alarm-here">
          {t('live.ms_alarm_here')}
        </span>
      ) : (
        <button className="btn btn-sm" onClick={() => multiScreen.claimAlarm()} data-testid="ms-claim-alarm">
          {t('live.ms_claim_alarm')}
        </button>
      )}
      <button
        className="btn btn-sm"
        onClick={() => {
          if (multiScreen.wall) forgetSpot(multiScreen.wall);
          window.close();
        }}
      >
        {t('live.ms_close_wall')}
      </button>
    </div>
  );
}
